import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { usePendingReleaseNote } from './usePendingReleaseNote';

interface Props {
  /** Only fetch once the user is actually authenticated. */
  enabled: boolean;
}

/**
 * Full-page "what's new" popup, shown once per release note per user.
 *
 * Dismissal is recorded server-side, so it does not return on the next login or
 * on another device. Rendered without rehype-raw — release-note markdown is
 * displayed as text, never as embedded HTML.
 */
/** Treat "within this many px of the bottom" as read — sub-pixel and zoom
 *  rounding means scrollTop rarely lands exactly on the maximum. */
const SCROLL_EPSILON = 24;

const ReleaseNoteModal: React.FC<Props> = ({ enabled }) => {
  const { note, isOpen, dismiss } = usePendingReleaseNote(enabled);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasReadToEnd, setHasReadToEnd] = useState(false);

  const checkScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const atBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_EPSILON;
    // Latch: once they've reached the bottom, scrolling back up must not
    // re-disable the button.
    if (atBottom) setHasReadToEnd(true);
  }, []);

  // A short note may not overflow at all, in which case there is nothing to
  // scroll and it counts as read immediately. Runs after the markdown paints.
  //
  // Also re-checks on resize, and watches the content for late layout shifts —
  // an image finishing loading changes scrollHeight after the first frame, which
  // would otherwise leave a note that fits looking unread.
  useEffect(() => {
    if (!isOpen || !note) return;

    const id = requestAnimationFrame(checkScroll);
    window.addEventListener('resize', checkScroll);

    const el = contentRef.current;
    const observer = el ? new ResizeObserver(checkScroll) : null;
    if (el && observer) {
      observer.observe(el);
      // Children carry the height that actually matters.
      Array.from(el.children).forEach((child) => observer.observe(child));
    }

    // Safety net: this modal has no backdrop/Escape exit, so if scroll detection
    // ever fails the user would be trapped. Unlock after a few seconds
    // regardless — the goal is to encourage reading, not to strand anyone.
    const unlock = setTimeout(() => setHasReadToEnd(true), 20000);

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', checkScroll);
      observer?.disconnect();
      clearTimeout(unlock);
    };
  }, [isOpen, note, checkScroll]);

  if (!note) return null;

  return (
    <Dialog
      open={isOpen}
      // No onClose: backdrop click and Escape would otherwise dismiss the note
      // without reading it, and dismissal is permanent — the note never returns.
      // "Got it" is the only way out, and it unlocks at the end of the content.
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <Box
        sx={{
          bgcolor: '#1a1a2e',
          color: '#fff',
          px: 4,
          py: 3,
          textAlign: 'center',
        }}
      >
        {/* Each Typography sets its own colour explicitly: MUI variants pull a
            colour from the theme palette, which overrides the `color: #fff`
            inherited from the Box and renders dark text on this dark header. */}
        <Typography
          variant="overline"
          sx={{ letterSpacing: '0.08em', color: 'rgba(255,255,255,0.75)', fontWeight: 700 }}
        >
          What's New
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: '#fff' }}>
          {note.title}
        </Typography>
        {note.version && (
          <Typography
            variant="body2"
            sx={{ mt: 0.5, color: 'rgba(255,255,255,0.7)' }}
          >
            {note.version}
          </Typography>
        )}
      </Box>

      <DialogContent
        dividers
        ref={contentRef}
        onScroll={checkScroll}
        sx={{ px: 4, py: 3 }}
      >
        {note.summary && (
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
            {note.summary}
          </Typography>
        )}

        <Box
          sx={{
            fontSize: 15,
            lineHeight: 1.75,
            '& h1': { fontSize: 22, mt: 2, mb: 1, fontWeight: 700 },
            '& h2': { fontSize: 19, mt: 2, mb: 1, fontWeight: 700 },
            '& h3': { fontSize: 16, mt: 2, mb: 1, fontWeight: 700 },
            '& p': { my: 1.25 },
            '& ul, & ol': { pl: 3, my: 1.25 },
            '& li': { mb: 0.5 },
            '& a': { color: '#3182ce' },
            '& code': {
              bgcolor: '#f5f5f5',
              px: 0.5,
              borderRadius: 0.5,
              fontSize: 13.5,
            },
            '& table': { borderCollapse: 'collapse', width: '100%', my: 2 },
            '& th, & td': {
              border: '1px solid #e0e0e0',
              p: 1,
              textAlign: 'left',
            },
            '& img': { maxWidth: '100%' },
          }}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{note.body}</ReactMarkdown>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 4, py: 2.5, justifyContent: 'space-between', gap: 2 }}
      >
        {/* Tells the user why the button is disabled, and doubles as an
            affordance to scroll. Reserves the row's height either way so the
            footer doesn't shift when it disappears. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            visibility: hasReadToEnd ? 'hidden' : 'visible',
          }}
        >
          <KeyboardArrowDownIcon fontSize="small" />
          <Typography variant="body2">Scroll to the end to continue</Typography>
        </Box>

        <Button
          onClick={() => void dismiss()}
          variant="contained"
          size="large"
          disabled={!hasReadToEnd}
        >
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseNoteModal;
